import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@medikei/ui';
import {
  Search,
  Eye,
  Ban,
  Check,
  Star,
  ChevronLeft,
  ChevronRight,
  FileText,
  Plus,
  X,
  Crown,
  Stethoscope,
  Clock,
  Award,
  Mail,
  Calendar,
  CheckCircle2,
  CheckCircle,
  RefreshCw,
  Activity,
  ExternalLink,
  Trash2,
  Copy,
  Sparkles,
  LogIn,
  ShieldAlert,
} from 'lucide-react';
import { httpClient, doctorsApi, adminApi, filesApi } from '@/api';
import { DoctorDocumentsModal } from '@/components/admin/DoctorDocumentsModal';
import { DoctorProfilePreviewModal } from '@/components/doctor/DoctorProfilePreviewModal';
import {
  ADMIN_IMPERSONATOR_TOKEN_KEY,
  ADMIN_IMPERSONATOR_USER_KEY,
} from '@/components/admin/ImpersonationBanner';

interface Doctor {
  id: string;
  name: string;
  email: string;
  crm: string;
  crmState?: string;
  crmUf?: string;
  specialty: string;
  status: 'PENDING' | 'APPROVED' | 'BLOCKED' | 'REJECTED';
  online: boolean;
  rating: number;
  totalConsultations: number;
  createdAt: string;
  avatarUrl?: string;
  bio?: string;
  blockedReason?: string;
}

interface DoctorSpecialty {
  id: string;
  doctorId: string;
  specialtyId: string;
  primary: boolean;
  status?: 'PENDING' | 'APPROVED' | 'REJECTED';
  rqe?: string | null;
  proofDocumentUrl?: string | null;
  telemedicineEnabled?: boolean;
  inPersonEnabled?: boolean;
  homeCareEnabled?: boolean;
  rejectionReason?: string | null;
  verifiedAt?: string | null;
  verifiedBy?: string | null;
  createdAt: string;
  specialty: {
    id: string;
    name: string;
    description: string | null;
    icon: string | null;
  };
}

interface Specialty {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
}

interface DoctorsResponse {
  doctors: Doctor[];
  total: number;
  page: number;
  pageSize: number;
}

export function AdminDoctorsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'ALL' | 'PENDING' | 'APPROVED' | 'BLOCKED' | 'REJECTED'>('ALL');
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);
  const [specialtyDialogDoctor, setSpecialtyDialogDoctor] = useState<Doctor | null>(null);
  const [documentsDialogDoctor, setDocumentsDialogDoctor] = useState<Doctor | null>(null);
  const [previewDoctor, setPreviewDoctor] = useState<Doctor | null>(null);
  const [previewDoctorDetails, setPreviewDoctorDetails] = useState<any | null>(null);
  const [rejectDialogDoctor, setRejectDialogDoctor] = useState<Doctor | null>(null);
  const [rejectReasonInput, setRejectReasonInput] = useState('');

  const handleOpenPreview = async (doctor: Doctor) => {
    try {
      const resp = await httpClient.get<any>(`/admin/doctors/${doctor.id}`);
      const detail = resp?.data || resp;
      setPreviewDoctorDetails(detail);
    } catch {
      setPreviewDoctorDetails(null);
    }
    setPreviewDoctor(doctor);
  };

  const { data: allSpecialties } = useQuery<Specialty[]>({
    queryKey: ['specialties'],
    queryFn: () => doctorsApi.getSpecialties(),
  });

  const { data: doctorSpecialtiesData, refetch: refetchDoctorSpecialties } = useQuery<DoctorSpecialty[]>({
    queryKey: ['admin', 'doctor', specialtyDialogDoctor?.id, 'specialties'],
    queryFn: () => adminApi.getDoctorSpecialties(specialtyDialogDoctor!.id),
    enabled: !!specialtyDialogDoctor,
  });

  const addSpecialtyMutation = useMutation({
    mutationFn: async ({ specialtyId, primary }: { specialtyId: string; primary?: boolean }) => {
      return adminApi.addSpecialtyToDoctor(specialtyDialogDoctor!.id, specialtyId, primary);
    },
    onSuccess: () => {
      refetchDoctorSpecialties();
      queryClient.invalidateQueries({ queryKey: ['admin', 'doctors'] });
    },
  });

  const removeSpecialtyMutation = useMutation({
    mutationFn: async (specialtyId: string) => {
      return adminApi.removeSpecialtyFromDoctor(specialtyDialogDoctor!.id, specialtyId);
    },
    onSuccess: () => {
      refetchDoctorSpecialties();
      queryClient.invalidateQueries({ queryKey: ['admin', 'doctors'] });
    },
  });

  const setPrimaryMutation = useMutation({
    mutationFn: async (specialtyId: string) => {
      return adminApi.setDoctorPrimarySpecialty(specialtyDialogDoctor!.id, specialtyId);
    },
    onSuccess: () => {
      refetchDoctorSpecialties();
      queryClient.invalidateQueries({ queryKey: ['admin', 'doctors'] });
    },
  });

  const [rejectingSpecialtyId, setRejectingSpecialtyId] = useState<string | null>(null);
  const [rejectionReasonInput, setRejectionReasonInput] = useState('');
  const [copiedRqeKey, setCopiedRqeKey] = useState<string | null>(null);

  const verifySpecialtyMutation = useMutation({
    mutationFn: async ({
      specialtyId,
      approved,
      rqe,
      rejectionReason,
    }: {
      specialtyId: string;
      approved: boolean;
      rqe?: string;
      rejectionReason?: string;
    }) => {
      return adminApi.verifyDoctorSpecialty(specialtyDialogDoctor!.id, specialtyId, {
        approved,
        rqe,
        rejectionReason,
        telemedicineEnabled: approved,
        inPersonEnabled: approved,
        homeCareEnabled: approved,
      });
    },
    onSuccess: () => {
      refetchDoctorSpecialties();
      queryClient.invalidateQueries({ queryKey: ['admin', 'doctors'] });
      setRejectingSpecialtyId(null);
      setRejectionReasonInput('');
    },
  });

  // Estado para Personificação Segura de Conta Médica (Admin Impersonation)
  const [impersonateDoctorTarget, setImpersonateDoctorTarget] = useState<Doctor | null>(null);
  const [isImpersonatingLoading, setIsImpersonatingLoading] = useState(false);

  const handleConfirmImpersonate = async () => {
    if (!impersonateDoctorTarget) return;
    try {
      setIsImpersonatingLoading(true);
      const currentToken = localStorage.getItem('accessToken');
      const currentUser = localStorage.getItem('user');

      // Backup seguro da sessão original do administrador
      if (currentToken) {
        localStorage.setItem(ADMIN_IMPERSONATOR_TOKEN_KEY, currentToken);
      }
      if (currentUser) {
        localStorage.setItem(ADMIN_IMPERSONATOR_USER_KEY, currentUser);
      }

      const res = await adminApi.impersonateDoctor(impersonateDoctorTarget.id);
      if (res.success && res.accessToken) {
        httpClient.saveAccessToken(res.accessToken);
        // Redireciona para o painel do médico (ou tela de análise caso pendente)
        window.location.href = '/medico/dashboard';
      }
    } catch (err: any) {
      alert(err?.message || 'Erro ao personificar conta médica.');
      setIsImpersonatingLoading(false);
    }
  };

  const { data, isLoading } = useQuery<DoctorsResponse>({
    queryKey: ['admin', 'doctors', page, filter, search],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: '10',
      });
      if (filter !== 'ALL') params.append('status', filter);
      if (search) params.append('search', search);

      const json = await httpClient.get<{ data: any[]; meta: { page: number; perPage: number; total: number } }>(`/admin/doctors?${params}`);
      return {
        doctors: json.data.map((doc: any) => ({
          id: doc.id,
          name: doc.displayName || 'Unknown',
          email: doc.email || 'N/A',
          crm: `${doc.crmState || 'SP'}/${doc.crm}`,
          crmState: doc.crmState || 'SP',
          crmUf: doc.crmState || 'SP',
          specialty: doc.specialty || 'N/A',
          status: doc.status,
          online: false,
          rating: 0,
          totalConsultations: 0,
          createdAt: doc.createdAt,
        })),
        total: json.meta.total,
        page: json.meta.page,
        pageSize: json.meta.perPage,
      };
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (doctorId: string) => {
      return httpClient.patch(`/admin/doctors/${doctorId}/approve`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'doctors'] });
    },
  });

  const blockMutation = useMutation({
    mutationFn: async (doctorId: string) => {
      return httpClient.patch(`/admin/doctors/${doctorId}/block`, { reason: 'Blocked by admin' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'doctors'] });
    },
  });

  const unblockMutation = useMutation({
    mutationFn: async (doctorId: string) => {
      return httpClient.patch(`/admin/doctors/${doctorId}/unblock`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'doctors'] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ doctorId, reason }: { doctorId: string; reason?: string }) => {
      return httpClient.patch(`/admin/doctors/${doctorId}/reject`, { reason });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'doctors'] });
    },
  });

  const filteredDoctors = data?.doctors.filter((doctor) => {
    const matchesSearch = doctor.name.toLowerCase().includes(search.toLowerCase()) ||
                          doctor.email.toLowerCase().includes(search.toLowerCase()) ||
                          doctor.crm.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === 'ALL' || doctor.status === filter;
    return matchesSearch && matchesFilter;
  }) || [];

  const stats = {
    total: data?.total || 0,
    pending: data?.doctors.filter((d) => d.status === 'PENDING').length || 0,
    approved: data?.doctors.filter((d) => d.status === 'APPROVED').length || 0,
    blocked: data?.doctors.filter((d) => d.status === 'BLOCKED' || d.status === 'REJECTED').length || 0,
    specialties: allSpecialties?.length || 0,
  };

  const getInitials = (name: string) => {
    if (!name) return 'MD';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'MD';
    if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
    const first = parts[0] ?? '';
    const last = parts[parts.length - 1] ?? '';
    return `${first[0] || 'M'}${last[0] || 'D'}`.toUpperCase();
  };

  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateStr));
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Gerenciamento de Médicos</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Aprovação cadastral, gestão de CRM e especialidades médicas da plataforma
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/configuracoes?tab=priority"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20 transition-colors shadow-xs"
            title="Organizar sequência e destaque dos médicos parceiros na página inicial"
          >
            <Award className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
            <span>Ordem na Vitrine</span>
          </Link>
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['admin', 'doctors'] })}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-foreground hover:bg-slate-50 dark:hover:bg-neutral-800 transition-colors shadow-xs"
            title="Atualizar listagem"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            <span>Atualizar</span>
          </button>
        </div>
      </div>

      {/* 1. KPIs Cards com design moderno sem duplo padding */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total de Médicos */}
        <div className="rounded-2xl bg-white dark:bg-neutral-900 border border-slate-200/90 dark:border-neutral-800 p-5 shadow-xs flex items-center gap-4 transition-all hover:shadow-sm">
          <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900/60 flex items-center justify-center shrink-0">
            <Stethoscope className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total de Médicos</p>
            <p className="text-2xl font-black text-foreground mt-0.5 tracking-tight">{stats.total.toLocaleString('pt-BR')}</p>
          </div>
        </div>

        {/* Pendentes de Aprovação */}
        <div className="rounded-2xl bg-white dark:bg-neutral-900 border border-slate-200/90 dark:border-neutral-800 p-5 shadow-xs flex items-center gap-4 transition-all hover:shadow-sm">
          <div className="w-12 h-12 rounded-2xl bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 border border-amber-100 dark:border-amber-900/60 flex items-center justify-center shrink-0">
            <Clock className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Aguardando Aprovação</p>
            <p className="text-2xl font-black text-amber-600 mt-0.5 tracking-tight">{stats.pending.toLocaleString('pt-BR')}</p>
          </div>
        </div>

        {/* Médicos Aprovados */}
        <div className="rounded-2xl bg-white dark:bg-neutral-900 border border-slate-200/90 dark:border-neutral-800 p-5 shadow-xs flex items-center gap-4 transition-all hover:shadow-sm">
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/60 flex items-center justify-center shrink-0">
            <CheckCircle2 className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Médicos Aprovados</p>
            <p className="text-2xl font-black text-emerald-600 mt-0.5 tracking-tight">{stats.approved.toLocaleString('pt-BR')}</p>
          </div>
        </div>

        {/* Especialidades */}
        <div className="rounded-2xl bg-white dark:bg-neutral-900 border border-slate-200/90 dark:border-neutral-800 p-5 shadow-xs flex items-center gap-4 transition-all hover:shadow-sm">
          <div className="w-12 h-12 rounded-2xl bg-purple-50 dark:bg-purple-950/60 text-purple-600 dark:text-purple-400 border border-purple-100 dark:border-purple-900/60 flex items-center justify-center shrink-0">
            <Award className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Especialidades</p>
            <p className="text-2xl font-black text-purple-600 mt-0.5 tracking-tight">{stats.specialties.toLocaleString('pt-BR')}</p>
          </div>
        </div>
      </div>

      {/* 2. Barra de Busca e Filtro de Status Estilizados */}
      <div className="flex items-center flex-wrap gap-3">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por nome, email ou CRM..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-xs transition-all"
          />
        </div>

        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as typeof filter)}
            className="px-4 py-2.5 bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-xs transition-all cursor-pointer"
          >
            <option value="ALL">Todos os status</option>
            <option value="PENDING">Pendentes de análise</option>
            <option value="APPROVED">Apenas aprovados</option>
            <option value="REJECTED">Rejeitados</option>
            <option value="BLOCKED">Bloqueados</option>
          </select>
          <span className="text-xs font-medium text-slate-500 px-2.5 py-1.5 bg-slate-100 dark:bg-neutral-800 rounded-lg">
            {filteredDoctors.length} {filteredDoctors.length === 1 ? 'médico' : 'médicos'}
          </span>
        </div>
      </div>

      {/* 3. Tabela de Médicos (sem duplo padding e visual de alta fidelidade) */}
      <div className="rounded-2xl border border-slate-200/90 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-slate-50/80 dark:bg-slate-800/60 border-b border-slate-200/80 dark:border-neutral-800">
              <tr>
                <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Médico</th>
                <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">CRM</th>
                <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Especialidade</th>
                <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider hidden md:table-cell">Disponibilidade</th>
                <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Avaliação</th>
                <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Consultas</th>
                <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider hidden xl:table-cell">Data</th>
                <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-neutral-800/70 text-sm">
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                      <p className="text-xs text-muted-foreground mt-2">Carregando médicos...</p>
                    </div>
                  </td>
                </tr>
              ) : filteredDoctors.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-slate-500">
                    <div className="max-w-sm mx-auto space-y-2">
                      <Stethoscope className="h-10 w-10 text-slate-300 mx-auto" />
                      <p className="font-semibold text-foreground text-sm">Nenhum médico encontrado</p>
                      <p className="text-xs text-muted-foreground">Tente alterar os termos de busca ou o filtro de status selecionado.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredDoctors.map((doctor) => {
                  return (
                    <tr key={doctor.id} className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors duration-150">
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-blue-600 via-primary to-indigo-600 text-white font-black text-xs flex items-center justify-center shadow-xs shrink-0 tracking-wider">
                            {getInitials(doctor.name)}
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-foreground text-sm truncate">{doctor.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{doctor.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="font-mono text-xs font-semibold px-2 py-0.5 rounded-md bg-slate-100 dark:bg-neutral-800 text-slate-700 dark:text-neutral-300 border border-slate-200 dark:border-neutral-700">
                          {doctor.crm}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-slate-600 dark:text-neutral-300 hidden sm:table-cell font-medium text-xs">
                        {doctor.specialty}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full font-medium ${
                          doctor.status === 'APPROVED' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800' :
                          doctor.status === 'PENDING' ? 'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800' :
                          doctor.status === 'REJECTED' ? 'bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800' :
                          'bg-slate-100 text-slate-700 border border-slate-200 dark:bg-neutral-800 dark:text-neutral-300'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            doctor.status === 'APPROVED' ? 'bg-emerald-500' :
                            doctor.status === 'PENDING' ? 'bg-amber-500 animate-pulse' :
                            doctor.status === 'REJECTED' ? 'bg-rose-500' : 'bg-slate-400'
                          }`} />
                          {doctor.status === 'APPROVED' ? 'Aprovado' : 
                           doctor.status === 'PENDING' ? 'Pendente' : 
                           doctor.status === 'REJECTED' ? 'Rejeitado' : 'Bloqueado'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 hidden md:table-cell">
                        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                          <span className={`w-2 h-2 rounded-full ${doctor.online ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                          {doctor.online ? 'Online' : 'Offline'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 hidden lg:table-cell">
                        <div className="flex items-center gap-1 text-xs font-semibold text-slate-700 dark:text-neutral-300">
                          <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />
                          <span>{doctor.rating > 0 ? doctor.rating.toFixed(1) : '-'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-slate-600 dark:text-neutral-300 hidden lg:table-cell text-xs font-semibold">
                        {doctor.totalConsultations > 0 ? doctor.totalConsultations : '-'}
                      </td>
                      <td className="px-4 py-3.5 text-slate-500 hidden xl:table-cell text-xs">{formatDate(doctor.createdAt)}</td>
                      <td className="px-4 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Visualizar / Detalhes */}
                          <button
                            onClick={() => setSelectedDoctor(doctor)}
                            className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/50 rounded-lg border border-transparent hover:border-blue-200 transition-colors"
                            title="Ver Ficha Cadastral"
                          >
                            <Eye className="h-4 w-4" />
                          </button>

                          {/* Pré-visualizar Perfil Público */}
                          <button
                            onClick={() => handleOpenPreview(doctor)}
                            className="p-1.5 text-primary hover:text-primary hover:bg-primary/10 rounded-lg border border-transparent hover:border-primary/20 transition-colors"
                            title="Pré-visualizar Perfil Público do Médico"
                          >
                            <Sparkles className="h-4 w-4" />
                          </button>

                          {/* Especialidades */}
                          <button
                            onClick={() => setSpecialtyDialogDoctor(doctor)}
                            className="p-1.5 text-purple-600 hover:text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-950/50 rounded-lg border border-transparent hover:border-purple-200 transition-colors"
                            title="Gerenciar Especialidades"
                          >
                            <Crown className="h-4 w-4" />
                          </button>

                          {/* Ver Documentos */}
                          <button
                            onClick={() => setDocumentsDialogDoctor(doctor)}
                            className="p-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:hover:bg-neutral-800 rounded-lg border border-transparent hover:border-slate-200 transition-colors"
                            title="Ver Documentos Anexados"
                          >
                            <FileText className="h-4 w-4" />
                          </button>

                          {/* Acessar Conta do Médico (Impersonate Seguro) */}
                          <button
                            onClick={() => setImpersonateDoctorTarget(doctor)}
                            className="p-1.5 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 rounded-lg border border-transparent hover:border-indigo-200 transition-colors cursor-pointer"
                            title="Acessar conta do médico (Login como Médico)"
                          >
                            <LogIn className="h-4 w-4" />
                          </button>

                          {/* Ações contextuais de aprovação / bloqueio */}
                          {doctor.status === 'PENDING' && (
                            <>
                              <button
                                onClick={() => approveMutation.mutate(doctor.id)}
                                className="p-1.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/50 rounded-lg border border-transparent hover:border-emerald-200 transition-colors"
                                title="Aprovar Médico"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setRejectDialogDoctor(doctor);
                                  setRejectReasonInput('Fotos ou documentos pendentes de regularização.');
                                }}
                                className="p-1.5 text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/50 rounded-lg border border-transparent hover:border-rose-200 transition-colors"
                                title="Rejeitar Cadastro e Solicitar Ajustes"
                              >
                                <Ban className="h-4 w-4" />
                              </button>
                            </>
                          )}

                          {doctor.status === 'APPROVED' && (
                            <button
                              onClick={() => blockMutation.mutate(doctor.id)}
                              className="p-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/50 rounded-lg border border-transparent hover:border-amber-200 transition-colors"
                              title="Bloquear Médico"
                            >
                              <Ban className="h-4 w-4" />
                            </button>
                          )}

                          {doctor.status === 'BLOCKED' && (
                            <button
                              onClick={() => unblockMutation.mutate(doctor.id)}
                              className="p-1.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/50 rounded-lg border border-transparent hover:border-emerald-200 transition-colors"
                              title="Desbloquear Médico"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 dark:border-neutral-800 bg-slate-50/50 dark:bg-neutral-900">
          <p className="text-xs text-muted-foreground font-medium">
            Mostrando {((page - 1) * 10) + 1} a {Math.min(page * 10, data?.total || 0)} de {data?.total || 0} médicos
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="p-1.5 border border-slate-200 dark:border-neutral-800 rounded-lg hover:bg-slate-100 dark:hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Página Anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-3 py-1 bg-primary text-primary-foreground font-bold text-xs rounded-lg shadow-xs">
              {page}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page * 10 >= (data?.total || 0)}
              className="p-1.5 border border-slate-200 dark:border-neutral-800 rounded-lg hover:bg-slate-100 dark:hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Próxima Página"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* 4. Modal "Ficha Cadastral do Médico" (Design Premium com Padding Adequado) */}
      <Dialog open={!!selectedDoctor} onOpenChange={() => setSelectedDoctor(null)}>
        <DialogContent className="max-w-xl p-0 overflow-hidden rounded-3xl border border-slate-200 dark:border-neutral-800 shadow-2xl">
          <div className="p-5 bg-gradient-to-r from-blue-600 via-primary to-indigo-600 text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Stethoscope className="w-5 h-5 text-blue-200" />
              <DialogTitle className="text-base font-bold text-white">Ficha Cadastral do Médico</DialogTitle>
            </div>
            <button
              type="button"
              onClick={() => setSelectedDoctor(null)}
              className="p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-colors cursor-pointer"
              title="Fechar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {selectedDoctor && (
            <div className="p-6 space-y-5">
              {/* Header do Médico */}
              <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-neutral-800">
                <div className="flex items-center gap-3.5">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-blue-600 via-primary to-indigo-600 text-white font-black text-lg flex items-center justify-center shadow-md shrink-0">
                    {getInitials(selectedDoctor.name)}
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-foreground">{selectedDoctor.name}</h3>
                    <a
                      href={`mailto:${selectedDoctor.email}`}
                      className="text-xs text-primary hover:underline flex items-center gap-1 mt-0.5"
                    >
                      <Mail className="w-3 h-3" />
                      <span>{selectedDoctor.email}</span>
                    </a>
                  </div>
                </div>

                <div>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                    selectedDoctor.status === 'APPROVED' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                    selectedDoctor.status === 'PENDING' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                    selectedDoctor.status === 'REJECTED' ? 'bg-rose-50 text-rose-700 border border-rose-200' :
                    'bg-slate-100 text-slate-700 border border-slate-200'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      selectedDoctor.status === 'APPROVED' ? 'bg-emerald-500' :
                      selectedDoctor.status === 'PENDING' ? 'bg-amber-500 animate-pulse' :
                      selectedDoctor.status === 'REJECTED' ? 'bg-rose-500' : 'bg-slate-400'
                    }`} />
                    {selectedDoctor.status === 'APPROVED' ? 'Aprovado' : 
                     selectedDoctor.status === 'PENDING' ? 'Pendente' : 
                     selectedDoctor.status === 'REJECTED' ? 'Rejeitado' : 'Bloqueado'}
                  </span>
                </div>
              </div>

              {/* Grid Estruturado com Padding e Bordas Adequadas */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                {/* CRM */}
                <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-neutral-800/50 border border-slate-200/60 dark:border-neutral-700/60 space-y-1">
                  <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider block">CRM / UF</span>
                  <p className="font-mono font-bold text-foreground text-sm">{selectedDoctor.crm}</p>
                </div>

                {/* Especialidade */}
                <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-neutral-800/50 border border-slate-200/60 dark:border-neutral-700/60 space-y-1">
                  <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider block flex items-center gap-1">
                    <Award className="w-3 h-3 text-purple-500" />
                    <span>Especialidade Principal</span>
                  </span>
                  <p className="font-semibold text-foreground text-sm">{selectedDoctor.specialty}</p>
                </div>

                {/* Data de Cadastro */}
                <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-neutral-800/50 border border-slate-200/60 dark:border-neutral-700/60 space-y-1">
                  <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider block flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-slate-400" />
                    <span>Data de Cadastro</span>
                  </span>
                  <p className="font-semibold text-foreground text-sm">{formatDate(selectedDoctor.createdAt)}</p>
                </div>

                {/* Total de Consultas */}
                <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-neutral-800/50 border border-slate-200/60 dark:border-neutral-700/60 space-y-1">
                  <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider block flex items-center gap-1">
                    <Activity className="w-3 h-3 text-blue-500" />
                    <span>Total de Consultas</span>
                  </span>
                  <p className="font-semibold text-foreground text-sm">{selectedDoctor.totalConsultations} atendimentos</p>
                </div>

                {/* Avaliação */}
                <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-neutral-800/50 border border-slate-200/60 dark:border-neutral-700/60 space-y-1">
                  <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider block flex items-center gap-1">
                    <Star className="w-3 h-3 text-amber-500" />
                    <span>Avaliação Média</span>
                  </span>
                  <p className="font-semibold text-foreground text-sm">
                    {selectedDoctor.rating > 0 ? `${selectedDoctor.rating.toFixed(1)} / 5.0` : 'Sem avaliações'}
                  </p>
                </div>

                {/* Status de Atendimento */}
                <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-neutral-800/50 border border-slate-200/60 dark:border-neutral-700/60 space-y-1">
                  <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider block">Disponibilidade</span>
                  <p className="font-semibold text-foreground text-sm flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${selectedDoctor.online ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    <span>{selectedDoctor.online ? 'Online no Plantão' : 'Offline'}</span>
                  </p>
                </div>
              </div>

              {/* Botões de Ações Contextuais no Modal */}
              <div className="pt-2 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 dark:border-neutral-800">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleOpenPreview(selectedDoctor)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 transition-colors cursor-pointer"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    <span>Preview do Perfil</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDocumentsDialogDoctor(selectedDoctor);
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-foreground hover:bg-slate-50 transition-colors"
                  >
                    <FileText className="h-3.5 w-3.5 text-slate-500" />
                    <span>Ver Documentos</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSpecialtyDialogDoctor(selectedDoctor);
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 hover:bg-purple-100 transition-colors"
                  >
                    <Crown className="h-3.5 w-3.5 text-purple-600" />
                    <span>Especialidades</span>
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  {selectedDoctor.status === 'PENDING' && (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          approveMutation.mutate(selectedDoctor.id);
                          setSelectedDoctor(null);
                        }}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 shadow-xs transition-colors"
                      >
                        <Check className="h-3.5 w-3.5" />
                        <span>Aprovar Médico</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setRejectDialogDoctor(selectedDoctor);
                          setRejectReasonInput('Fotos ou documentos pendentes de regularização.');
                          setSelectedDoctor(null);
                        }}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-xl bg-rose-600 text-white hover:bg-rose-700 shadow-xs transition-colors"
                      >
                        <Ban className="h-3.5 w-3.5" />
                        <span>Rejeitar</span>
                      </button>
                    </>
                  )}

                  {selectedDoctor.status === 'APPROVED' && (
                    <button
                      type="button"
                      onClick={() => {
                        blockMutation.mutate(selectedDoctor.id);
                        setSelectedDoctor(null);
                      }}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-xl bg-amber-600 text-white hover:bg-amber-700 shadow-xs transition-colors"
                    >
                      <Ban className="h-3.5 w-3.5" />
                      <span>Bloquear Médico</span>
                    </button>
                  )}

                  {selectedDoctor.status === 'BLOCKED' && (
                    <button
                      type="button"
                      onClick={() => {
                        unblockMutation.mutate(selectedDoctor.id);
                        setSelectedDoctor(null);
                      }}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 shadow-xs transition-colors"
                    >
                      <Check className="h-3.5 w-3.5" />
                      <span>Desbloquear Médico</span>
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => setSelectedDoctor(null)}
                    className="px-3.5 py-2 text-xs font-semibold rounded-xl border border-slate-200 dark:border-neutral-700 text-muted-foreground hover:bg-slate-100 dark:hover:bg-neutral-800 transition-colors"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Rejeição de Cadastro com Motivo Personalizado */}
      <Dialog open={!!rejectDialogDoctor} onOpenChange={() => setRejectDialogDoctor(null)}>
        <DialogContent className="max-w-md p-0 overflow-hidden rounded-3xl border border-slate-200 dark:border-neutral-800 shadow-2xl bg-card">
          <div className="p-5 bg-rose-600 text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-white" />
              <DialogTitle className="text-base font-bold text-white">
                Rejeitar Cadastro Médico
              </DialogTitle>
            </div>
            <button
              type="button"
              onClick={() => setRejectDialogDoctor(null)}
              className="p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-colors cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {rejectDialogDoctor && (
            <div className="p-6 space-y-4">
              <p className="text-xs text-muted-foreground leading-relaxed">
                Ao rejeitar o cadastro de <strong className="text-foreground">{rejectDialogDoctor.name}</strong>, o médico verá o motivo detalhado em seu painel e poderá ajustar os dados ou reenviar documentos.
              </p>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Motivo da Recusa / O que ficou pendente <span className="text-destructive">*</span>
                </label>
                <textarea
                  rows={3}
                  value={rejectReasonInput}
                  onChange={(e) => setRejectReasonInput(e.target.value)}
                  placeholder="Ex: Foto da carteira do CRM ilegível. Por favor, reenvie o verso com nitidez."
                  className="w-full p-3 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                />
              </div>

              <div className="pt-2 flex items-center justify-end gap-2 border-t border-border">
                <button
                  type="button"
                  onClick={() => setRejectDialogDoctor(null)}
                  className="px-3.5 py-2 text-xs font-semibold rounded-xl border border-slate-200 dark:border-neutral-700 text-muted-foreground hover:bg-slate-100 dark:hover:bg-neutral-800 transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    rejectMutation.mutate({
                      doctorId: rejectDialogDoctor.id,
                      reason: rejectReasonInput.trim() || 'Documentação pendente ou fotos inconsistentes.',
                    });
                    setRejectDialogDoctor(null);
                  }}
                  disabled={rejectMutation.isPending}
                  className="px-4 py-2 text-xs font-bold rounded-xl bg-rose-600 hover:bg-rose-700 text-white shadow-xs transition-colors cursor-pointer disabled:opacity-50"
                >
                  {rejectMutation.isPending ? 'Rejeitando...' : 'Confirmar Rejeição'}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Pré-visualização do Perfil Médico para Auditoria do Admin */}
      <DoctorProfilePreviewModal
        open={!!previewDoctor}
        onClose={() => {
          setPreviewDoctor(null);
          setPreviewDoctorDetails(null);
        }}
        isAdminView={true}
        onApprove={() => {
          if (previewDoctor) {
            approveMutation.mutate(previewDoctor.id);
            setPreviewDoctor(null);
            setPreviewDoctorDetails(null);
          }
        }}
        onReject={() => {
          if (previewDoctor) {
            setRejectDialogDoctor(previewDoctor);
            setRejectReasonInput('Fotos ou dados do perfil pendentes de regularização.');
            setPreviewDoctor(null);
            setPreviewDoctorDetails(null);
          }
        }}
        isApproving={approveMutation.isPending}
        data={
          previewDoctor
            ? {
                displayName: previewDoctorDetails?.displayName || previewDoctorDetails?.name || previewDoctor.name,
                name: previewDoctorDetails?.name || previewDoctor.name,
                crm: previewDoctorDetails?.crm || previewDoctor.crm?.replace(/[^\d]/g, ''),
                crmState: previewDoctorDetails?.crmState || previewDoctorDetails?.crmUf || previewDoctor.crmState || 'SP',
                tagline: previewDoctorDetails?.tagline || previewDoctorDetails?.profile?.tagline || null,
                bio: previewDoctorDetails?.bio || (previewDoctorDetails as any)?.profile?.bio || previewDoctor.bio || null,
                photoUrl: previewDoctorDetails?.photoUrl || previewDoctorDetails?.profile?.photoUrl || previewDoctor.avatarUrl || null,
                coverUrl: previewDoctorDetails?.coverUrl || previewDoctorDetails?.profile?.coverUrl || null,
                specialty: previewDoctorDetails?.specialty || previewDoctor.specialty,
                specialties: previewDoctorDetails?.specialties || [],
                languages: previewDoctorDetails?.languages || previewDoctorDetails?.profile?.languages || ['Português'],
                awards: previewDoctorDetails?.awards || previewDoctorDetails?.profile?.awards || [],
                publications: previewDoctorDetails?.publications || previewDoctorDetails?.profile?.publications || null,
                minAge: previewDoctorDetails?.minAge,
                maxAge: previewDoctorDetails?.maxAge,
                allowedAgeGroups: previewDoctorDetails?.allowedAgeGroups,
                hasTelemedicine: previewDoctorDetails?.hasTelemedicine,
                hasInPerson: previewDoctorDetails?.hasInPerson,
                hasHomeCare: previewDoctorDetails?.hasHomeCare,
                clinicName: previewDoctorDetails?.clinicName,
                clinicAddress: previewDoctorDetails?.clinicAddress,
                clinicCity: previewDoctorDetails?.clinicCity,
                clinicState: previewDoctorDetails?.clinicState,
                homeCareRadiusKm: previewDoctorDetails?.homeCareRadiusKm,
              }
            : null
        }
      />

      {/* 5. Modal de Especialidades com Padding e Cabeçalho Redesenhados */}
      <Dialog open={!!specialtyDialogDoctor} onOpenChange={() => setSpecialtyDialogDoctor(null)}>
        <DialogContent className="max-w-md p-0 overflow-hidden rounded-3xl border border-slate-200 dark:border-neutral-800 shadow-2xl">
          <div className="p-5 bg-gradient-to-r from-purple-600 via-indigo-600 to-primary text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-200" />
              <DialogTitle className="text-base font-bold text-white">
                Especialidades - {specialtyDialogDoctor?.name}
              </DialogTitle>
            </div>
            <button
              type="button"
              onClick={() => setSpecialtyDialogDoctor(null)}
              className="p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-colors cursor-pointer"
              title="Fechar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {specialtyDialogDoctor && (
            <div className="p-6 space-y-5">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Especialidades & Validação de RQE
                  </h4>
                  <span className="text-xs text-muted-foreground">
                    {doctorSpecialtiesData?.length || 0} especialidades
                  </span>
                </div>

                {doctorSpecialtiesData && doctorSpecialtiesData.length > 0 ? (
                  <div className="space-y-3">
                    {doctorSpecialtiesData.map((ds) => {
                      const isGeneral =
                        ds.specialty.name.toLowerCase().includes('clínica geral') ||
                        ds.specialty.name.toLowerCase().includes('clinica geral');
                      const isApproved = isGeneral || ds.status === 'APPROVED';
                      const isPending = !isGeneral && ds.status === 'PENDING';
                      const isRejected = !isGeneral && ds.status === 'REJECTED';
                      const isRejectingThis = rejectingSpecialtyId === ds.specialtyId;
                      const isCopiedRqe = copiedRqeKey === ds.specialtyId;

                      return (
                        <div
                          key={ds.id}
                          className="p-4 bg-slate-50 dark:bg-neutral-800/60 rounded-2xl border border-slate-200/80 dark:border-neutral-700/80 space-y-3 shadow-xs"
                        >
                          {/* Cabeçalho da especialidade */}
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`w-2.5 h-2.5 rounded-full ${isApproved ? 'bg-emerald-500' : isPending ? 'bg-amber-500 animate-pulse' : 'bg-slate-300'}`} />
                              <span className="font-bold text-foreground text-sm">{ds.specialty.name}</span>
                              {ds.primary && (
                                <span className="text-[11px] bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 px-2 py-0.5 rounded-full font-bold">
                                  Primária
                                </span>
                              )}
                            </div>

                            {/* Badge de status */}
                            <div>
                              {isGeneral ? (
                                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2.5 py-0.5 rounded-full border border-emerald-500/20">
                                  <CheckCircle className="h-3 w-3" /> Habilitado por CRM (Generalista)
                                </span>
                              ) : isApproved ? (
                                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2.5 py-0.5 rounded-full border border-emerald-500/20">
                                  <CheckCircle className="h-3 w-3" /> Aprovado / RQE Validado
                                </span>
                              ) : isRejected ? (
                                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-600 dark:text-rose-400 bg-rose-500/10 px-2.5 py-0.5 rounded-full border border-rose-500/20">
                                  <Ban className="h-3 w-3" /> Recusado
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-600 dark:text-amber-400 bg-amber-500/10 px-2.5 py-0.5 rounded-full border border-amber-500/20 animate-pulse">
                                  <Clock className="h-3 w-3" /> Aguardando Validação de RQE
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Dados da Comprovação RQE e Anexos (se não for Clínica Geral) */}
                          {!isGeneral && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs pt-1">
                              {/* RQE */}
                              <div className="p-2.5 rounded-xl bg-white dark:bg-neutral-900 border border-slate-200/60 dark:border-neutral-700/60 flex items-center justify-between">
                                <div>
                                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block">
                                    Número do RQE
                                  </span>
                                  <span className="font-mono font-bold text-foreground text-xs">
                                    {ds.rqe ? ds.rqe : <span className="text-muted-foreground/60 font-normal">Não informado</span>}
                                  </span>
                                </div>
                                {ds.rqe && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      navigator.clipboard?.writeText(ds.rqe || '');
                                      setCopiedRqeKey(ds.specialtyId);
                                      setTimeout(() => setCopiedRqeKey(null), 2000);
                                    }}
                                    className="p-1 rounded-md text-slate-400 hover:text-foreground hover:bg-slate-100 transition-colors"
                                    title="Copiar RQE"
                                  >
                                    {isCopiedRqe ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                                  </button>
                                )}
                              </div>

                              {/* Comprovante / Anexo */}
                              <div className="p-2.5 rounded-xl bg-white dark:bg-neutral-900 border border-slate-200/60 dark:border-neutral-700/60 flex items-center justify-between">
                                <div className="min-w-0 pr-2">
                                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block">
                                    Comprovante Anexado
                                  </span>
                                  <span className="text-xs truncate block text-foreground">
                                    {ds.proofDocumentUrl ? 'Documento enviado' : <span className="text-muted-foreground/60">Sem anexo</span>}
                                  </span>
                                </div>
                                {ds.proofDocumentUrl && (
                                  <a
                                    href={filesApi.getFileUrl(ds.proofDocumentUrl)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline shrink-0"
                                    title="Abrir arquivo anexado"
                                  >
                                    <span>Ver</span>
                                    <ExternalLink className="h-3 w-3" />
                                  </a>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Motivo de recusa exibido se houver */}
                          {ds.rejectionReason && (
                            <div className="p-2.5 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/40 text-xs text-rose-700 dark:text-rose-300">
                              <span className="font-bold">Motivo da Recusa:</span> {ds.rejectionReason}
                            </div>
                          )}

                          {/* Painel de Rejeição rápida */}
                          {isRejectingThis && (
                            <div className="p-3 bg-white dark:bg-neutral-900 rounded-xl border border-rose-200 dark:border-rose-900/60 space-y-2">
                              <label className="block text-xs font-bold text-rose-700 dark:text-rose-300">
                                Motivo da Recusa do RQE:
                              </label>
                              <input
                                type="text"
                                placeholder="Ex: Número de RQE não localizado no CRM ou diploma ilegível"
                                value={rejectionReasonInput}
                                onChange={(e) => setRejectionReasonInput(e.target.value)}
                                className="w-full text-xs px-3 py-2 border border-slate-300 dark:border-neutral-700 rounded-lg outline-none focus:ring-1 focus:ring-rose-500 bg-background text-foreground"
                              />
                              <div className="flex items-center justify-end gap-2 pt-1">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setRejectingSpecialtyId(null);
                                    setRejectionReasonInput('');
                                  }}
                                  className="px-2.5 py-1 text-xs rounded-lg border border-slate-200 text-muted-foreground hover:bg-slate-100"
                                >
                                  Cancelar
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    verifySpecialtyMutation.mutate({
                                      specialtyId: ds.specialtyId,
                                      approved: false,
                                      rejectionReason: rejectionReasonInput.trim() || 'Documentação ou RQE inconsistente',
                                    });
                                  }}
                                  disabled={verifySpecialtyMutation.isPending}
                                  className="px-2.5 py-1 text-xs font-bold rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
                                >
                                  Confirmar Recusa
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Ações do Administrador */}
                          <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-slate-200/60 dark:border-neutral-700/60">
                            <div className="flex items-center gap-1.5">
                              {/* Botão de Aprovação de RQE pelo Admin */}
                              {!isGeneral && !isApproved && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    verifySpecialtyMutation.mutate({
                                      specialtyId: ds.specialtyId,
                                      approved: true,
                                      rqe: ds.rqe || undefined,
                                    });
                                  }}
                                  disabled={verifySpecialtyMutation.isPending}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 shadow-xs transition-colors disabled:opacity-50 cursor-pointer"
                                  title="Aprovar RQE e habilitar atendimentos imediatamente"
                                >
                                  <Check className="h-3.5 w-3.5" />
                                  <span>Aprovar RQE</span>
                                </button>
                              )}

                              {/* Botão de Rejeição de RQE */}
                              {!isGeneral && !isApproved && !isRejectingThis && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setRejectingSpecialtyId(ds.specialtyId);
                                    setRejectionReasonInput('');
                                  }}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-xl border border-rose-200 text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
                                  title="Recusar comprovação de RQE"
                                >
                                  <X className="h-3.5 w-3.5" />
                                  <span>Recusar</span>
                                </button>
                              )}

                              {/* Indicador de modalidades habilitadas */}
                              {isApproved && (
                                <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                                  ✓ Telemedicina, Presencial e Domiciliar Liberados
                                </span>
                              )}
                            </div>

                            <div className="flex items-center gap-1">
                              {!ds.primary && (
                                <button
                                  type="button"
                                  onClick={() => setPrimaryMutation.mutate(ds.specialtyId)}
                                  disabled={setPrimaryMutation.isPending}
                                  className="p-1.5 text-slate-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors cursor-pointer"
                                  title="Definir como primária"
                                >
                                  <Crown className="h-4 w-4" />
                                </button>
                              )}
                              {!isGeneral && (
                                <button
                                  type="button"
                                  onClick={() => removeSpecialtyMutation.mutate(ds.specialtyId)}
                                  disabled={removeSpecialtyMutation.isPending}
                                  className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
                                  title="Remover especialidade"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-4 bg-slate-50 rounded-xl text-center border border-dashed border-slate-200">
                    <p className="text-xs text-muted-foreground">Nenhuma especialidade vinculada a este médico.</p>
                  </div>
                )}
              </div>

              <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-neutral-800">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Adicionar Especialidade</h4>
                <AddSpecialtyForm
                  currentSpecialtyIds={doctorSpecialtiesData?.map(ds => ds.specialtyId) || []}
                  allSpecialties={allSpecialties || []}
                  onAdd={(specialtyId, primary) => addSpecialtyMutation.mutate({ specialtyId, primary })}
                  isLoading={addSpecialtyMutation.isPending}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <DoctorDocumentsModal
        doctor={documentsDialogDoctor}
        open={!!documentsDialogDoctor}
        onClose={() => setDocumentsDialogDoctor(null)}
      />

      {/* Modal de Confirmação Segura de Personificação (Impersonate) */}
      {impersonateDoctorTarget && (
        <Dialog open={Boolean(impersonateDoctorTarget)} onOpenChange={(open) => !open && setImpersonateDoctorTarget(null)}>
          <DialogContent className="max-w-md rounded-2xl p-6 bg-card border border-border shadow-xl space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center border border-indigo-500/20 shrink-0">
                <LogIn className="w-5 h-5" />
              </div>
              <div>
                <DialogTitle className="text-base font-bold text-foreground">
                  Acessar Conta do Médico
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Visualização completa da conta médica como Administrador
                </p>
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-muted/40 border border-border space-y-1 text-xs">
              <p className="font-bold text-foreground">{impersonateDoctorTarget.name}</p>
              <p className="text-muted-foreground">{impersonateDoctorTarget.email}</p>
              <p className="font-mono text-muted-foreground text-[11px]">
                CRM: {impersonateDoctorTarget.crm} • {impersonateDoctorTarget.specialty || 'Clínica Geral'}
              </p>
            </div>

            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-800 dark:text-amber-300 text-xs flex items-start gap-2.5">
              <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-[11px] leading-relaxed">
                Uma barra fixa no topo da tela indicará que você está no <strong>Modo Administrador</strong> e permitirá <strong>retornar a este painel com um único clique</strong> a qualquer momento.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setImpersonateDoctorTarget(null)}
                disabled={isImpersonatingLoading}
                className="rounded-xl text-xs"
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={handleConfirmImpersonate}
                loading={isImpersonatingLoading}
                className="rounded-xl font-bold bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5 text-xs shadow-xs"
              >
                <LogIn className="w-3.5 h-3.5" />
                <span>Acessar Conta Agora</span>
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function AddSpecialtyForm({
  currentSpecialtyIds,
  allSpecialties,
  onAdd,
  isLoading,
}: {
  currentSpecialtyIds: string[];
  allSpecialties: Specialty[];
  onAdd: (specialtyId: string, primary?: boolean) => void;
  isLoading: boolean;
}) {
  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState('');
  const [setAsPrimary, setSetAsPrimary] = useState(false);

  const availableSpecialties = allSpecialties.filter(s => !currentSpecialtyIds.includes(s.id));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedSpecialtyId) {
      onAdd(selectedSpecialtyId, setAsPrimary);
      setSelectedSpecialtyId('');
      setSetAsPrimary(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
      <select
        value={selectedSpecialtyId}
        onChange={(e) => setSelectedSpecialtyId(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        disabled={isLoading || availableSpecialties.length === 0}
      >
        <option value="">Selecione uma especialidade</option>
        {availableSpecialties.map((s) => (
          <option key={s.id} value={s.id}>{s.name}</option>
        ))}
      </select>
      <label className="flex items-center gap-2 text-sm text-gray-600">
        <input
          type="checkbox"
          checked={setAsPrimary}
          onChange={(e) => setSetAsPrimary(e.target.checked)}
          className="rounded border-gray-300"
        />
        Definir como primária
      </label>
      <button
        type="submit"
        disabled={!selectedSpecialtyId || isLoading}
        className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Plus className="h-4 w-4" />
        Adicionar
      </button>
    </form>
  );
}
